import { push } from "connected-react-router";
import React, { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  APPLICATION_MODE_NEW,
  DOCUMENT_CONTENT_TYPE_PDF,
  DOCUMENT_TYPE_WORK_EXPERIENCE,
  individualFileSizeAllowedInMB,
  ROUTE_PATH,
  REQUEST_STATUS
} from "../../../app/constants";
import {
  Header,
  Button,
  NavigationLinkButton,
  Paragraph,
  ErrorMark
} from "../../../components/RegularComponents";
import TopNavigationBar from "../../TopNavigationBar";
import { ERROR_STRING } from "../../../app/errors";
import MRAWorkHistoryDetailsForm from "../../../components/WorkDetails/MraWorkHistoryDetailsForm";
import { saveBusinessLicenceApplicationData } from "../../../redux/slice/businessLicenceApplicationSlice";
import {
  acceptedFileFormats,
  convertFileDataForUrl,
  extractBase64,
  fileNameWithLengthLimit,
  fileTypeEnum,
  validateFileName
} from "../../../app/documentUploadUtils";
import {
  DocumentViewerFileNameLink,
  DownloadFileNameLink
} from "../../../components/DocumentViewer/DocumentFileComponent";
import { fetchDocumentData } from "../../../redux/slice/supportingDocumentsSlice";
import DocumentViewer from "../../../components/DocumentViewer/DocumentViewer";
import { applicationTypeFromLicenceSubType } from "../../../redux/slice/applicationsUtils";
import { usePathApplication } from "../../../redux/hooks/usePathApplication";
import { agencyApplicationSelector } from "../../../redux/selector/agencyApplicationSelector";
import {
  setAgencyWorkHistoryDetails,
  setAgencyWorkHistoryDocuments,
  setMortgageProperty
} from "../../../redux/slice/primaryAgencyApplicationSlice";

export const MainAgentUploadWorkHistory = (props) => {
  const applicationMode = APPLICATION_MODE_NEW;
  const { application } = usePathApplication();
  // const isPrincipalBroker =  (ROUTE_PATH.PB_APP.search(application) !== -1);
  const { agencyApplication } = useSelector(agencyApplicationSelector) || {};
  const licencingSubType = agencyApplication?.licencingSubType;

  const serverSaveRequest = useSelector(
    (state) => state.primaryAgencyApplication.serverRequestStatus
  );
  const applicationType = applicationTypeFromLicenceSubType(licencingSubType);

  const [applicationSaveError, setApplicationSaveError] = useState(false);

  const [openDocumentViewer, setOpenDocumentViewer] = useState(false);
  const [documentViewerDocument, setDocumentViewerDocument] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const { uploadWorkHistory, experienceDocuments } = useMemo(() => {
    return {
      uploadWorkHistory: agencyApplication.businessLicenceData?.workHistoryData ?? [],
      experienceDocuments: agencyApplication?.documents?.filter(
        (document) =>
          document.documentType === DOCUMENT_TYPE_WORK_EXPERIENCE &&
          document.changeType !== "DELETE"
      )
    };
  }, [agencyApplication]);

  // array of objects with a name and file
  const [uploadWorkHistoryDetails, setUploadWorkHistoryDetails] = useState(
    uploadWorkHistory.length > 0
      ? uploadWorkHistory.map((element, index) => {
          const docForIndex = experienceDocuments.filter(
            (document) => +document.entityId === index
          );

          return {
            startDate: element.startDate,
            duration: element.duration,
            description: element.description,
            file: docForIndex?.length > 0 ? docForIndex[0] : {}
          };
        })
      : [
          {
            startDate: null,
            duration: null,
            description: null,
            file: {}
          }
        ]
  );

  const [properFileCheck, setProperFileCheck] = useState(
    uploadWorkHistory.length > 0 ? uploadWorkHistory.map((x) => false) : [false]
  );
  const [formCheck, setFormCheck] = useState(
    uploadWorkHistory.length > 0 ? uploadWorkHistory.map((x) => false) : [false]
  );
  const [showValidationError, setShowValidationError] = useState(false);

  const { isModal = false, onEditSave = () => {} } = props;

  const dispatch = useDispatch();

  const SubSeparator = () => {
    return (
      <hr
        className="bottomMarginNormal"
        style={{
          borderColor: "#0058a466",
          borderWidth: "3px",
          width: "65%",
          marginLeft: "0px"
        }}
      />
    );
  };

  // const validateData = () => {
  //   let error = false;

  //   //Iterate thorugh uploadWorkHistoryDetails
  //   uploadWorkHistoryDetails.forEach((element, index) => {
  //     if (!element.duration || !element.description) {
  //       let updateFormCheck = [...formCheck];
  //       updateFormCheck[index] = true;
  //       setFormCheck(updateFormCheck);

  //       error = true;
  //     }
  //   });

  //   return error;
  // };

  const saveData = () => {
    dispatch(
      setAgencyWorkHistoryDetails(
        uploadWorkHistoryDetails.map(({ startDate, duration, description }) => ({
          startDate,
          duration,
          description
        }))
      )
    );

    dispatch(
      setAgencyWorkHistoryDocuments(uploadWorkHistoryDetails.map(({ file }) => ({ ...file })))
    );

    //Reset property if any added
    if (agencyApplication.businessLicenceData?.mblraLicenceNumber) {
      dispatch(
        setMortgageProperty({
          key: "mblraLicenceNumber",
          value: null
        })
      );
    }
  };

  const handleSaveAndNext = () => {
    setApplicationSaveError(false);
    saveData();

    // On to the next item
    dispatch(saveBusinessLicenceApplicationData(applicationType, applicationMode))
      .then((data) => {
        dispatch(push(`/${application}${ROUTE_PATH.MRA_ADD_MAIN_AGENT_COURSE_CERTIFICATE_UPLOAD}`));
      })
      .catch((err) => {
        setApplicationSaveError(true);
      });
  };

  const handleSaveandClose = () => {
    setApplicationSaveError(false);
    saveData();
    onEditSave();
  };

  const fileSizeReplacement = (string, fileSize) => {
    const replaceRegExp = /<file_size>/g;
    const errorString = string.replace(replaceRegExp, fileSize + "MB");
    return errorString;
  };

  const handleFileInput = (e, index) => {
    if (e.target.files.length > 0) {
      let file = e.target.files[0];

      // individual file size can not be more than 15 MB
      if (file.size > individualFileSizeAllowedInMB * 2 ** 20) {
        alert(
          fileSizeReplacement(
            ERROR_STRING.SUPPORTING_DOC_VERY_LARGE_INDIVIDUAL_FILE,
            individualFileSizeAllowedInMB
          )
        );
        return;
      } else if (!validateFileName(file.name)) {
        alert("File name is not valid. Please select another file.");
        return;
      }

      let reader = new FileReader();
      reader.onload = (readerData) => {
        let uploadWorkHistoryDetailsUpdate = [...uploadWorkHistoryDetails];

        uploadWorkHistoryDetailsUpdate[index].file = {
          filename: fileNameWithLengthLimit(file.name),
          documentContentType: fileTypeEnum(file.type),
          data: extractBase64(readerData.target.result),
          changeType: "SAVE",
          entityId: index,
          documentType: DOCUMENT_TYPE_WORK_EXPERIENCE
        };

        setUploadWorkHistoryDetails(uploadWorkHistoryDetailsUpdate);
      };
      reader.readAsDataURL(file);

      // reset validation errors
      setShowValidationError(false);
    }
  };

  const fileComponent = (file, index) => (
    <div className="supportingDocumentsFileContentWrapper">
      <span className="fileName">
        {file.documentContentType === DOCUMENT_CONTENT_TYPE_PDF ? (
          <DocumentViewerFileNameLink
            id={"work-history-file-" + file.filename}
            key={"work-history-file-" + file.filename}
            file={file}
            onClick={() => {
              if (file.data) {
                let fileDataUrl = convertFileDataForUrl(file.data, file.documentContentType);
                setDocumentViewerDocument(fileDataUrl);
                setOpenDocumentViewer(true);
                setSelectedFile(file);
              } else {
                // download data and then view it.
                if (file.guid) {
                  dispatch(fetchDocumentData(file.guid))
                    .then((downloadFile) => {
                      if (downloadFile) {
                        let fileDataUrl = convertFileDataForUrl(
                          downloadFile.data,
                          downloadFile.documentContentType
                        );
                        setDocumentViewerDocument(fileDataUrl);
                        setOpenDocumentViewer(true);
                        setSelectedFile(downloadFile);
                      }
                    })
                    .catch((error) => {
                      console.error(error);
                    });
                }
              }
            }}
          />
        ) : (
          <DownloadFileNameLink
            id={"work-history-file-" + file.filename}
            componentKey={"work-history-file-" + file.filename}
            file={file}
          />
        )}
      </span>

      <NavigationLinkButton
        id={"work-history-file-remove-link"}
        text={"- Remove this file"}
        onClick={() => {
          let uploadWorkHistoryDetailsUpdate = [...uploadWorkHistoryDetails];

          uploadWorkHistoryDetailsUpdate[index].file = {
            ...file,
            changeType: "DELETE"
          };

          setUploadWorkHistoryDetails(uploadWorkHistoryDetailsUpdate);
        }}
      />
    </div>
  );

  const WorkHistoryBlock = ({ data = [] }) => {
    return (
      <>
        {data.map((element, index) => (
          <>
            <div className="row">
              <div className="col-lg-6">
                <MRAWorkHistoryDetailsForm
                  startDate={element.startDate}
                  duration={element.duration}
                  description={element.description}
                  file={element.file}
                  validationError={formCheck[index]}
                  index={index}
                  onSaveItem={(index, saveElement, field) => {
                    let uploadWorkHistoryDetailsUpdate = [...uploadWorkHistoryDetails];
                    uploadWorkHistoryDetailsUpdate[index][field] = saveElement;
                    setUploadWorkHistoryDetails(uploadWorkHistoryDetailsUpdate);
                    setShowValidationError(false);

                    // reset form check
                    let updateFormCheck = [...formCheck];
                    setFormCheck(updateFormCheck.map((x) => false));
                  }}
                />
              </div>

              <div className="col-lg-2">
                {index > 0 && (
                  <NavigationLinkButton
                    id={"branch-remove-link"}
                    text={"- Remove work history"}
                    onClick={() => {
                      let uploadWorkHistoryUpdate = [...uploadWorkHistoryDetails];
                      uploadWorkHistoryUpdate.splice(index, 1);
                      setUploadWorkHistoryDetails(uploadWorkHistoryUpdate);

                      let properFileCheckUpdate = [...properFileCheck];
                      properFileCheckUpdate.splice(index, 1);
                      setProperFileCheck(properFileCheckUpdate);

                      setShowValidationError(false);

                      let formCheckUpdate = [...formCheck];
                      formCheckUpdate.splice(index, 1);
                      setFormCheck(formCheckUpdate);
                    }}
                  />
                )}
              </div>
            </div>

            {element.file &&
              Object.keys(element.file).length > 0 &&
              element.file.changeType !== "DELETE" && (
                <div className="row bottomMarginNormal">
                  <div className="col-lg-8">{fileComponent(element.file, index)}</div>
                </div>
              )}

            <div className="row">
              <div className="col-lg-8">
                <Paragraph
                  style={{ paddingBottom: "10px" }}
                  id="principal-broker-work-exp-letter"
                  text={[
                    "<h2>Letter from previous employer</h3>",
                    "Upload a letter from a previous employer that confirms the applicant's experience brokering mortgages at a federally regulated company."
                  ]}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-lg-8">
                <fieldset className="ns-fieldset" id="work-history-form">
                  <div className={"ns-fieldset"} style={{ paddingBottom: "20px" }}>
                    <label className="ns-label" id="upload-work-history-file-label" htmlFor={index}>
                      Upload a file
                    </label>
                    <div className="ns-file-input">
                      <input
                        disabled={
                          element.file &&
                          Object.keys(element.file).length > 0 &&
                          element.file.changeType !== "DELETE"
                        }
                        className="ns-input"
                        style={{ width: "15em", display: "inline" }}
                        type="file"
                        value=""
                        accept={acceptedFileFormats(DOCUMENT_TYPE_WORK_EXPERIENCE).join(",")}
                        id={index}
                        name="upload-work-history-file-input"
                        multiple={false}
                        onInput={(e) => handleFileInput(e, index)}
                      />
                    </div>
                  </div>
                </fieldset>
              </div>
            </div>
            <SubSeparator />
          </>
        ))}
      </>
    );
  };

  const workHistoryText =
    "Provide information about your experience brokering mortgages for a federally regulated financial institution in Nova Scotia";

  return (
    <>
      <div className={!isModal ? "container" : ""}>
        {!isModal && (
          <>
            <TopNavigationBar
              {...props}
              applicationType={applicationType}
              applicationMode={applicationMode}
            />
            <div className="row">
              <div className="col-xs-12 col-lg-9">
                <Header
                  style={{ paddingBottom: "30px" }}
                  id="company-fiscal-end-date-header"
                  header="Work History"
                />
                <Paragraph
                  style={{ paddingBottom: "10px" }}
                  id="company-fiscal-end-date-header"
                  text={[workHistoryText]}
                />
              </div>
            </div>
          </>
        )}

        <WorkHistoryBlock data={uploadWorkHistoryDetails} />

        <div className="row">
          <div className="col-xs-12 col-lg-9">
            <Paragraph
              style={{ paddingBottom: "20px" }}
              id="upload-mortgage-education-text"
              text={[
                "The maximum file size allowed is 15 MB per file and 50 MB total. Supported document types are Microsoft Word, Microsoft Office Excel, or Adobe PDF.  All digital files must be in an uncompressed format, i.e no zip or ARC files."
              ]}
            />
          </div>
        </div>
        <div className="row bottomMarginMedium">
          <div className={"col-lg-8"}>
            <NavigationLinkButton
              id={"criminaly-history-add-more-link"}
              text={"+ Add another work history"}
              onClick={() => {
                setUploadWorkHistoryDetails([
                  ...uploadWorkHistoryDetails,
                  {
                    startDate: null,
                    duration: null,
                    description: null,
                    file: {}
                  }
                ]);

                setProperFileCheck([...properFileCheck, false]);
                setFormCheck([...formCheck, false]);
              }}
            />
          </div>
        </div>

        <div className="row">
          <div className={"col-lg-4"}>
            <Button
              id="company-fiscal-end-date-button"
              style={{ margin: "0 10px 10px 0" }}
              buttonType={"regular_primary"}
              text={!isModal ? "Save and next" : "Save and close"}
              onClick={!isModal ? handleSaveAndNext : handleSaveandClose}
              showSpinner={
                !applicationSaveError && serverSaveRequest === REQUEST_STATUS.IN_PROGRESS
              }
              disabled={serverSaveRequest === REQUEST_STATUS.IN_PROGRESS}
            />
            {showValidationError && ErrorMark()}
            {showValidationError && (
              <div className="ns-error-message">
                <span className="ns-visually-hidden">Error:</span>
                {ERROR_STRING.UPLOAD_CERTIFICATE_ERROR}.&nbsp;&nbsp;
                {ERROR_STRING.CHECK_FIELDS}
              </div>
            )}
            {applicationSaveError && ErrorMark()}
            {applicationSaveError && (
              <div className="ns-error-message help-block topMarginNormal">
                <span className="ns-visually-hidden">Error:</span>
                {ERROR_STRING.APPLICATION_SAVE_ERROR}
              </div>
            )}
          </div>
        </div>
        <DocumentViewer
          fileData={documentViewerDocument}
          isOpen={openDocumentViewer}
          fileName={selectedFile?.filename}
          handleClose={() => setOpenDocumentViewer(false)}
        />
      </div>
    </>
  );
};
